import React, { useContext, useEffect, useState, useRef, useCallback } from "react";
import { ProjectContext } from "../../context/project_context";
import TimecodeInput from "../../components/TimecodeInput";
import InsertRowModal from "../../components/InsertRowModal";
import debounce from 'lodash/debounce';

const ProjectFixTranscript = () => {
  const { 
    project, 
    updateProject, 
    transcripts, 
    fetchTranscripts, 
    handleSaveTranscript, 
    handleMarkTranscriptAsError, 
    handleMarkTranscriptAsResolved,
    updateTranscriptTimecodes,
    insertTranscript,
    deleteTranscript,
    // Media playback
    activeTranscriptId,
    autoScrollEnabled,
    seekToTranscript,
    currentPlaybackTime,
    formatTimeCode
  } = useContext(ProjectContext);

  const [editedTranscripts, setEditedTranscripts] = useState({});
  const [savingStates, setSavingStates] = useState({});
  const [debouncedFunctions] = useState(() => ({}));
  const [transcriptErrors, setTranscriptErrors] = useState({});
  const [resolvedErrors, setResolvedErrors] = useState({});
  
  // Insert row modal state
  const [showInsertModal, setShowInsertModal] = useState(false);
  const [insertPosition, setInsertPosition] = useState({ startTime: 0, endTime: 1 });

  // Refs for auto-scrolling
  const rowRefs = useRef({});
  const tableContainerRef = useRef(null);

  useEffect(() => {
    if (project?.id) {
      fetchTranscripts();
    }
  }, [project?.id]);

  useEffect(() => {
    // Initialize transcript errors from existing data if available
    const initialErrors = {};
    const initialResolved = {};
    transcripts.forEach(transcript => {
      initialErrors[transcript.id] = transcript.has_error || false;
      initialResolved[transcript.id] = transcript.has_error_resolved || false;
    });
    setTranscriptErrors(initialErrors);
    setResolvedErrors(initialResolved);
  }, [transcripts]);

  // Auto-scroll to active row
  useEffect(() => {
    if (autoScrollEnabled && activeTranscriptId && rowRefs.current[activeTranscriptId]) {
      rowRefs.current[activeTranscriptId].scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [activeTranscriptId, autoScrollEnabled]);

  // Listen for focus active transcript event (Shift+Enter shortcut)
  useEffect(() => {
    const handleFocusActive = () => {
      if (activeTranscriptId && rowRefs.current[activeTranscriptId]) {
        const row = rowRefs.current[activeTranscriptId];
        const textarea = row.querySelector('.transcript-textarea');
        if (textarea) {
          textarea.focus();
          textarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    };

    window.addEventListener('focusActiveTranscript', handleFocusActive);
    return () => window.removeEventListener('focusActiveTranscript', handleFocusActive);
  }, [activeTranscriptId]);

  // Listen for seek to focused transcript event (Ctrl+Enter shortcut)
  useEffect(() => {
    const handleSeekToFocused = () => {
      // Find which transcript's textarea is currently focused
      const focusedTextarea = document.activeElement;
      if (focusedTextarea?.classList.contains('transcript-textarea')) {
        // Find the parent row and get the transcript ID
        const row = focusedTextarea.closest('tr');
        if (row) {
          const transcriptId = Object.keys(rowRefs.current).find(
            id => rowRefs.current[id] === row
          );
          if (transcriptId) {
            const transcript = transcripts.find(t => t.id === parseInt(transcriptId));
            if (transcript) {
              seekToTranscript(transcript);
            }
          }
        }
      }
    };

    window.addEventListener('seekToFocusedTranscript', handleSeekToFocused);
    return () => window.removeEventListener('seekToFocusedTranscript', handleSeekToFocused);
  }, [transcripts, seekToTranscript]);

  const handleComplete = async () => {
    await updateProject({
      ...project,
      project_status: "sentence_building"
    });
  };

  const handleBack = async () => {
    await updateProject({
      ...project,
      project_status: "identify_transcript_errors"
    });
  };

  const handleContentChange = (transcriptId, newContent) => {
    setEditedTranscripts(prev => ({
      ...prev,
      [transcriptId]: newContent
    }));

    if (debouncedFunctions[transcriptId]) {
      debouncedFunctions[transcriptId].cancel();
    }

    if (!debouncedFunctions[transcriptId]) {
      debouncedFunctions[transcriptId] = debounce(async (content) => {
        try {
          setSavingStates(prev => ({ ...prev, [transcriptId]: true }));
          const success = await handleSaveTranscript(transcriptId, content);
          if (success) {
            setSavingStates(prev => ({ ...prev, [transcriptId]: false }));
          }
        } catch (error) {
          console.error('Failed to save transcript:', error);
          setSavingStates(prev => ({ ...prev, [transcriptId]: false }));
        }
      }, 1000);
    }

    debouncedFunctions[transcriptId](newContent);
  };

  const handleTimecodeChange = useCallback(async (transcriptId, field, newValue) => {
    const transcript = transcripts.find(t => t.id === transcriptId);
    if (!transcript) return;

    const startTime = field === 'start_time' ? newValue : parseFloat(transcript.start_time);
    const endTime = field === 'end_time' ? newValue : parseFloat(transcript.end_time);

    if (endTime <= startTime) {
      alert('End time must be greater than start time');
      return;
    }

    try {
      setSavingStates(prev => ({ ...prev, [`timecode_${transcriptId}`]: true }));
      await updateTranscriptTimecodes(transcriptId, startTime, endTime);
    } catch (error) {
      console.error('Failed to update timecodes:', error);
      alert('Failed to update timecodes');
    } finally {
      setSavingStates(prev => ({ ...prev, [`timecode_${transcriptId}`]: false }));
    }
  }, [transcripts, updateTranscriptTimecodes]);

  const handleErrorToggle = (transcriptId, hasError) => {
    setTranscriptErrors(prev => ({
      ...prev,
      [transcriptId]: hasError
    }));

    if (!hasError) {
      setResolvedErrors(prev => ({
        ...prev,
        [transcriptId]: false
      }));
    }

    if (debouncedFunctions[`error_${transcriptId}`]) {
      debouncedFunctions[`error_${transcriptId}`].cancel();
    }

    if (!debouncedFunctions[`error_${transcriptId}`]) {
      debouncedFunctions[`error_${transcriptId}`] = debounce(async (hasError) => {
        try {
          setSavingStates(prev => ({ ...prev, [`error_${transcriptId}`]: true }));
          const success = await handleMarkTranscriptAsError(transcriptId, hasError, resolvedErrors[transcriptId] || false);
          if (success) {
            setSavingStates(prev => ({ ...prev, [`error_${transcriptId}`]: false }));
          }
        } catch (error) {
          console.error('Failed to mark/unmark transcript as error:', error);
          setSavingStates(prev => ({ ...prev, [`error_${transcriptId}`]: false }));
        }
      }, 1000);
    }

    debouncedFunctions[`error_${transcriptId}`](hasError);
  };

  const handleResolvedToggle = (transcriptId, isResolved) => {
    setResolvedErrors(prev => ({
      ...prev,
      [transcriptId]: isResolved
    }));

    if (debouncedFunctions[`resolved_${transcriptId}`]) {
      debouncedFunctions[`resolved_${transcriptId}`].cancel();
    }

    if (!debouncedFunctions[`resolved_${transcriptId}`]) {
      debouncedFunctions[`resolved_${transcriptId}`] = debounce(async (isResolved) => {
        try {
          setSavingStates(prev => ({ ...prev, [`resolved_${transcriptId}`]: true }));
          const success = await handleMarkTranscriptAsResolved(transcriptId, isResolved);
          if (success) {
            setSavingStates(prev => ({ ...prev, [`resolved_${transcriptId}`]: false }));
          }
        } catch (error) {
          console.error('Failed to mark/unmark transcript as resolved:', error);
          setSavingStates(prev => ({ ...prev, [`resolved_${transcriptId}`]: false }));
        }
      }, 1000);
    }

    debouncedFunctions[`resolved_${transcriptId}`](isResolved);
  };

  const handleKeyDown = (e, currentIndex, transcriptId) => {
    const textareas = document.querySelectorAll('.transcript-textarea');

    switch (e.key) {
      case 'ArrowUp':
        if (currentIndex > 0) {
          textareas[currentIndex - 1].focus();
        }
        break;
      case 'ArrowDown':
        if (currentIndex < textareas.length - 1) {
          textareas[currentIndex + 1].focus();
        }
        break;
      case 'Enter':
        if (!e.shiftKey && !e.ctrlKey) {
          e.preventDefault();
          if (currentIndex < textareas.length - 1) {
            textareas[currentIndex + 1].focus();
          }
        }
        break;
    }
  };

  const handleInsertRow = (afterTranscript) => {
    // Calculate default timecodes based on position
    let startTime = 0;
    let endTime = 1;

    if (afterTranscript) {
      startTime = parseFloat(afterTranscript.end_time) || 0;
      // Find the next transcript to determine end time
      const currentIndex = transcripts.findIndex(t => t.id === afterTranscript.id);
      const nextTranscript = transcripts[currentIndex + 1];
      if (nextTranscript) {
        endTime = parseFloat(nextTranscript.start_time) || startTime + 1;
      } else {
        endTime = startTime + 2;
      }
    }

    setInsertPosition({ startTime, endTime });
    setShowInsertModal(true);
  };

  const handleInsertConfirm = async (startTime, endTime, text) => {
    await insertTranscript(startTime, endTime, text);
  };

  const handleDeleteRow = async (transcriptId) => {
    if (window.confirm('Are you sure you want to delete this transcript row?')) {
      await deleteTranscript(transcriptId);
    }
  };

  const handleRowClick = (transcript) => {
    seekToTranscript(transcript);
  };

  return (
    <div className="project-fix-transcript">
      <h2>Fix Transcript</h2>
      <div className="transcript-content">
        <p>Project Name: {project.name}</p>
        
        {/* Insert row at beginning button */}
        <div className="mb-3">
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => handleInsertRow(null)}
          >
            + Insert Row at Beginning
          </button>
        </div>
        
        <div className="transcripts-list mt-3" ref={tableContainerRef}>
          <table className="table table-bordered no-padding">
            <thead>
              <tr>
                <th style={{ width: '50px' }}>T#</th>
                <th style={{ width: '240px' }}>Timecodes</th>
                <th>Content</th>
                <th style={{ width: '70px' }}>Error</th>
                <th style={{ width: '70px' }}>Fixed</th>
                <th style={{ width: '150px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {transcripts.map((transcript, index) => {
                const isActive = transcript.id === activeTranscriptId;
                const hasError = transcriptErrors[transcript.id];
                const isResolved = resolvedErrors[transcript.id];
                
                let rowClass = '';
                if (isActive) {
                  rowClass = 'table-info';
                } else if (hasError && !isResolved) {
                  rowClass = 'table-warning';
                } else if (hasError && isResolved) {
                  rowClass = 'table-success';
                }

                return (
                  <React.Fragment key={transcript.id}>
                    <tr 
                      ref={el => rowRefs.current[transcript.id] = el}
                      className={rowClass}
                      style={{ 
                        cursor: 'pointer',
                        transition: 'background-color 0.3s ease'
                      }}
                    >
                      <td 
                        style={{ width: '50px' }}
                        onClick={() => handleRowClick(transcript)}
                      >
                        {index + 1}
                      </td>
                      <td style={{ width: '240px' }}>
                        <div className="d-flex flex-column gap-1">
                          <div className="d-flex align-items-center gap-1">
                            <small className="text-muted" style={{ width: '35px' }}>Start:</small>
                            <TimecodeInput
                              value={parseFloat(transcript.start_time)}
                              onChange={(val) => handleTimecodeChange(transcript.id, 'start_time', val)}
                            />
                          </div>
                          <div className="d-flex align-items-center gap-1">
                            <small className="text-muted" style={{ width: '35px' }}>End:</small>
                            <TimecodeInput
                              value={parseFloat(transcript.end_time)}
                              onChange={(val) => handleTimecodeChange(transcript.id, 'end_time', val)}
                            />
                          </div>
                          {savingStates[`timecode_${transcript.id}`] && (
                            <small className="text-muted">Saving...</small>
                          )}
                        </div>
                      </td>
                      <td className="p-1">
                        <textarea
                          className="form-control transcript-textarea border-0 rounded-0"
                          value={editedTranscripts[transcript.id] ?? transcript.text}
                          onChange={(e) => handleContentChange(transcript.id, e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, index, transcript.id)}
                          rows="2"
                        />
                      </td>
                      <td className="text-center" style={{ width: '70px' }}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={transcriptErrors[transcript.id] || false}
                          onChange={(e) => handleErrorToggle(transcript.id, e.target.checked)}
                        />
                      </td>
                      <td className="text-center" style={{ width: '70px' }}>
                        {transcriptErrors[transcript.id] && (
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={resolvedErrors[transcript.id] || false}
                            onChange={(e) => handleResolvedToggle(transcript.id, e.target.checked)}
                          />
                        )}
                      </td>
                      <td style={{ width: '150px' }}>
                        <div className="d-flex flex-column gap-1">
                          {(editedTranscripts[transcript.id] && savingStates[transcript.id]) && (
                            <span className="text-muted small">Saving...</span>
                          )}
                          <div className="btn-group btn-group-sm">
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => handleInsertRow(transcript)}
                              title="Insert row after this one"
                            >
                              +
                            </button>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => handleDeleteRow(transcript.id)}
                              title="Delete this row"
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="d-flex gap-2 mt-3">
          <button 
            className="btn btn-secondary"
            onClick={handleBack}
          >
            Back to Identify Errors
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleComplete}
          >
            Next to Sentence Building
          </button>
        </div>
      </div>

      {/* Insert Row Modal */}
      <InsertRowModal
        isOpen={showInsertModal}
        onClose={() => setShowInsertModal(false)}
        onInsert={handleInsertConfirm}
        defaultStartTime={insertPosition.startTime}
        defaultEndTime={insertPosition.endTime}
        type="transcript"
      />
    </div>
  );
};

export default ProjectFixTranscript;
